import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  TextField,
  Typography,
  Box,
  Divider,
  Alert
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const UpgradeApprovalModal = ({ open, onClose, upgradePreview, onApprove }) => {
  const [upgradeStatus, setUpgradeStatus] = useState('payable');
  const [discountType, setDiscountType] = useState('amount'); // 'amount' or 'percentage'
  const [discountValue, setDiscountValue] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  if (!upgradePreview) return null;

  const handleApprove = () => {
    // Validation
    if (upgradeStatus === 'complimentary' && !reason.trim()) {
      setError('Please provide a reason for complimentary upgrade');
      return;
    }
    if (upgradeStatus === 'discounted') {
      const discount = parseFloat(discountValue);
      if (!discountValue || isNaN(discount) || discount <= 0) {
        setError(`Please enter a valid discount ${discountType === 'percentage' ? 'percentage' : 'amount'}`);
        return;
      }
      if (discountType === 'percentage' && discount >= 100) {
        setError('Discount percentage must be less than 100%');
        return;
      }
      if (discountType === 'amount' && discount >= parseFloat(upgradePreview.upgrade_amount)) {
        setError('Discount amount cannot be equal to or greater than upgrade amount');
        return;
      }
      if (!reason.trim()) {
        setError('Please provide a reason for discounted upgrade');
        return;
      }
    }

    // Calculate actual discount amount
    let discountAmount = 0;
    if (upgradeStatus === 'discounted') {
      const upgradeAmt = parseFloat(upgradePreview.upgrade_amount);
      if (discountType === 'percentage') {
        discountAmount = upgradeAmt * (parseFloat(discountValue) / 100);
      } else {
        discountAmount = parseFloat(discountValue);
      }
    }

    // Build approval data
    const approvalData = {
      upgrade_status: upgradeStatus,
      upgrade_amount: parseFloat(upgradePreview.upgrade_amount),
      upgrade_discount_amount: discountAmount,
      upgrade_reason: reason.trim()
    };

    // Reset local state
    setUpgradeStatus('payable');
    setDiscountType('amount');
    setDiscountValue('');
    setReason('');
    setError('');

    // Call onApprove - parent will handle closing the modal
    onApprove(approvalData);
    // Don't call handleClose() here - let parent handle modal closing
  };

  const handleClose = () => {
    setUpgradeStatus('payable');
    setDiscountType('amount');
    setDiscountValue('');
    setReason('');
    setError('');
    onClose();
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const calculateDiscountAmount = () => {
    if (upgradeStatus !== 'discounted' || !discountValue) return 0;
    
    const upgradeAmt = parseFloat(upgradePreview.upgrade_amount);
    const discount = parseFloat(discountValue) || 0;
    
    if (discountType === 'percentage') {
      return upgradeAmt * (discount / 100);
    }
    return discount;
  };

  const calculateFinalAmount = () => {
    if (upgradeStatus === 'complimentary') return 0;
    
    const upgradeAmt = parseFloat(upgradePreview.upgrade_amount);
    const taxRate = parseFloat(upgradePreview.tax_rate);
    
    if (upgradeStatus === 'discounted') {
      const discountAmt = calculateDiscountAmount();
      const netUpgrade = upgradeAmt - discountAmt;
      const tax = netUpgrade * (taxRate / 100);
      return netUpgrade + tax;
    }
    
    return parseFloat(upgradePreview.total_additional_cost);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fff3e0' }}>
        <WarningAmberIcon sx={{ color: '#E4C984' }} />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Service Upgrade Detected
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 4 }}>
        {/* Info Alert */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Note:</strong> This approval is for planning. The guest will only be charged the upgrade amount when the treatment is successfully scheduled and booked.
          </Typography>
        </Alert>

        {/* Service Details */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Original Service:
          </Typography>
          <Box sx={{ pl: 2, mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {upgradePreview.original_treatment_name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {upgradePreview.original_tier_level} • {formatCurrency(upgradePreview.original_tier_value)}
            </Typography>
          </Box>

          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            New Service:
          </Typography>
          <Box sx={{ pl: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {upgradePreview.new_treatment_name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {upgradePreview.new_tier_level} • {formatCurrency(upgradePreview.new_tier_value)}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Pricing Summary */}
        <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">Upgrade Amount:</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {formatCurrency(upgradePreview.upgrade_amount)}
            </Typography>
          </Box>
          
          {/* Show discount if applicable */}
          {upgradeStatus === 'discounted' && discountValue && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#2e7d32' }}>
                Discount ({discountType === 'percentage' ? `${discountValue}%` : formatCurrency(discountValue)}):
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                - {formatCurrency(calculateDiscountAmount())}
              </Typography>
            </Box>
          )}
          
          {upgradeStatus === 'complimentary' && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#2e7d32' }}>
                Complimentary Discount:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                - {formatCurrency(upgradePreview.upgrade_amount)}
              </Typography>
            </Box>
          )}
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">
              Tax ({upgradePreview.tax_rate}%):
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {upgradeStatus === 'complimentary' 
                ? '₹0.00' 
                : upgradeStatus === 'discounted' && discountValue
                ? formatCurrency((parseFloat(upgradePreview.upgrade_amount) - calculateDiscountAmount()) * (parseFloat(upgradePreview.tax_rate) / 100))
                : formatCurrency(upgradePreview.tax_on_upgrade)
              }
            </Typography>
          </Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Total Additional Cost:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {formatCurrency(calculateFinalAmount())}
            </Typography>
          </Box>
        </Box>

        {/* Approval Options */}
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
          How should this upgrade be billed?
        </Typography>

        <FormControl component="fieldset" fullWidth sx={{ pl: 2 }}>
          <RadioGroup value={upgradeStatus} onChange={(e) => setUpgradeStatus(e.target.value)}>
            <FormControlLabel
              value="payable"
              control={<Radio sx={{ color: '#E4C984', '&.Mui-checked': { color: '#E4C984' }, padding: '0px', marginRight: '8px' }} />}
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: '20px' }}>
                    Payable
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Guest pays full upgrade amount ({formatCurrency(upgradePreview.total_additional_cost)})
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', mb: 1.5 }}
            />

            <FormControlLabel
              value="complimentary"
              control={<Radio sx={{ color: '#E4C984', '&.Mui-checked': { color: '#E4C984' }, padding: '0px', marginRight: '8px' }} />}
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: '20px' }}>
                    Complimentary
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Free upgrade - no charge
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', mb: 1.5 }}
            />

            <FormControlLabel
              value="discounted"
              control={<Radio sx={{ color: '#E4C984', '&.Mui-checked': { color: '#E4C984' }, padding: '0px', marginRight: '8px' }} />}
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: '20px' }}>
                    Discounted
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Partial payment with discount
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', mb: 1.5 }}
            />
          </RadioGroup>
        </FormControl>

        {/* Discount Input */}
        {upgradeStatus === 'discounted' && (
          <Box sx={{ mt: 2, pl: 4 }}>
            {/* Discount Type Selector */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button
                variant={discountType === 'amount' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => {
                  setDiscountType('amount');
                  setDiscountValue('');
                }}
                sx={{
                  flex: 1,
                  bgcolor: discountType === 'amount' ? '#E4C984' : 'transparent',
                  color: discountType === 'amount' ? '#171717' : '#E4C984',
                  borderColor: '#E4C984',
                  '&:hover': {
                    bgcolor: discountType === 'amount' ? '#cbb16f' : 'rgba(228, 201, 132, 0.1)',
                    borderColor: '#E4C984'
                  }
                }}
              >
                Amount (₹)
              </Button>
              <Button
                variant={discountType === 'percentage' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => {
                  setDiscountType('percentage');
                  setDiscountValue('');
                }}
                sx={{
                  flex: 1,
                  bgcolor: discountType === 'percentage' ? '#E4C984' : 'transparent',
                  color: discountType === 'percentage' ? '#171717' : '#E4C984',
                  borderColor: '#E4C984',
                  '&:hover': {
                    bgcolor: discountType === 'percentage' ? '#cbb16f' : 'rgba(228, 201, 132, 0.1)',
                    borderColor: '#E4C984'
                  }
                }}
              >
                Percentage (%)
              </Button>
            </Box>

            {/* Discount Value Input */}
            <TextField
              fullWidth
              size="small"
              label={discountType === 'percentage' ? 'Discount Percentage' : 'Discount Amount'}
              type="number"
              value={discountValue}
              onChange={(e) => setDiscountValue(e.target.value)}
              placeholder={discountType === 'percentage' ? 'e.g. 10' : 'Enter amount'}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>
                  {discountType === 'percentage' ? '%' : '₹'}
                </Typography>,
              }}
              inputProps={{
                min: 0,
                max: discountType === 'percentage' ? 99 : parseFloat(upgradePreview.upgrade_amount),
                step: discountType === 'percentage' ? 1 : 0.01
              }}
              sx={{ mb: 1 }}
            />
            {discountValue && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Discount: {formatCurrency(calculateDiscountAmount())}
                </Typography>
                <br />
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Guest will pay: {formatCurrency(calculateFinalAmount())}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Reason Input */}
        {(upgradeStatus === 'complimentary' || upgradeStatus === 'discounted') && (
          <Box sx={{ mt: 2, pl: 4 }}>
            <TextField
              fullWidth
              size="small"
              label="Reason *"
              multiline
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason for complimentary/discounted upgrade"
            />
          </Box>
        )}

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleApprove}
          variant="contained"
          sx={{
            bgcolor: '#E4C984',
            color: '#171717',
            '&:hover': { bgcolor: '#cbb16f' }
          }}
        >
          Approve Upgrade
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpgradeApprovalModal;

